import { DataTableRow, DataTableVariant } from '../../common/table/TableTypes';
import { NoResults } from '../shared/NoResults';
import { colors, Typography } from '@sweep-io/sweep-design';
import { useRef, useCallback } from 'react';
import { FunnelFieldLabels } from '../../../constants/deprecated_fieldsManagementConsts';
import { Percentage } from '../../common/Percentage';
import { Utilization } from './Utilization';
import { OCCUPIED_SPACE_SINGLE_OBJECT_VIEW_CANVAS } from '../helpers';
import { onFieldClickProps } from '../useDocumentation';
import { FieldLabelWithNameInTooltip } from '../shared/FieldLabelWithNameInTooltip';
import { VirtualScrollDataTable } from '../../common/table/VirtualScrollDataTable';
import { DependenciesContentViewType, SelectedDependency } from '../dependencies/types';
import {
  ConfigurationItem,
  ConfigurationItemField,
  ConfigurationItemRollup,
} from '../../parser/ParserTypes';
import { Stack } from '@mui/material';
import isNil from 'lodash/isNil';
import { DataTableId } from '../../common/table/utils';
import { useObjectContentHandleDeepLink } from './useObjectContentHandleDeepLink';
import { useFilterTabs } from './filters/useFilterTabs';
import { useIsCanvasUnited } from '../../../hooks/useIsCanvasUnited';
import { CenteredCircularProgress } from '../../common/CenteredCircularProgress';

interface FieldsTableProps {
  fields: (ConfigurationItemField | ConfigurationItemRollup)[];
  onFieldClick: (props: onFieldClickProps) => void;
  isManagePackageInstalled: boolean;
  objectName: string;
  firstHistoryItem?: SelectedDependency;
  crmOrgId: string;
}

const fieldsColumns = [
  { field: 'label', headerName: 'Name', width: '180px' },
  { field: 'usage', headerName: 'Utilization', width: '160px' },
];

const renderRows = ({
  items,
  isManagePackageInstalled,
  activeFieldName,
}: {
  items?: (ConfigurationItemField | ConfigurationItemRollup)[];
  isManagePackageInstalled: boolean;
  activeFieldName?: string;
}): (DataTableRow & Pick<ConfigurationItem, 'id' | 'name'>)[] => {
  if (!items) return [];

  return items?.map((row) => ({
    id: row.id,
    label: (
      <Stack>
        <FieldLabelWithNameInTooltip name={row.name} label={row.label} isRollup={!!row.isRollup} />
        <Typography
          variant="caption"
          whiteSpace="break-spaces"
          color={`${colors.grey[700]} !important`}
        >
          {FunnelFieldLabels[row.fieldType as SweepFieldTypes] ?? row.parentType}
        </Typography>
      </Stack>
    ),
    name: row.name,
    isRowActive: activeFieldName === row.name,
    usage: (
      <Stack width="120px">
        {!isNil(row.usage) || !isManagePackageInstalled ? (
          <Percentage
            preCalculated={row.usage}
            active={isManagePackageInstalled}
            typographyVariant="caption"
          />
        ) : (
          <CenteredCircularProgress size={16} />
        )}
      </Stack>
    ),
    isClickable: true,
    isRollup: !!row.isRollup,
  }));
};

export const FieldsTable = ({
  fields,
  objectName,
  onFieldClick,
  isManagePackageInstalled,
  firstHistoryItem,
  crmOrgId,
}: FieldsTableProps) => {
  const isCanvasUnited = useIsCanvasUnited();
  const elements = useFilterTabs();
  const containerRef = useRef<any>(null);
  const columns = fieldsColumns;
  const _columns = isManagePackageInstalled
    ? columns
    : columns.map((column) =>
        column.field === 'usage'
          ? {
              ...column,
              headerName: <Utilization columnName={column.headerName} />,
            }
          : column,
      );

  const onRowClick = useCallback(
    (e: React.MouseEvent<HTMLTableCellElement, MouseEvent>, row: DataTableRow) => {
      onFieldClick({
        id: row.id,
        fieldName: row.name,
        objectName,
        crmOrgId,
        isRollup: row.isRollup,
        contentType: DependenciesContentViewType.default,
      });
    },
    [onFieldClick, objectName, crmOrgId],
  );

  const rows = renderRows({
    items: fields,
    isManagePackageInstalled,
    activeFieldName: firstHistoryItem?.name,
  });
  useObjectContentHandleDeepLink({ rows, onRowClick, elements: elements.results });

  return (
    <Stack
      sx={{
        height: isCanvasUnited
          ? `calc(100vh - ${OCCUPIED_SPACE_SINGLE_OBJECT_VIEW_CANVAS})`
          : '100%',
        overflow: 'hidden',
        pr: 0,
        pl: 0,
      }}
    >
      <VirtualScrollDataTable
        tableId={DataTableId.DocumentationFieldsTable}
        hideChevronButton
        containerRef={containerRef}
        columns={_columns}
        rows={rows}
        tableEmptyStateJsx={<NoResults />}
        onRowClick={onRowClick}
        variant={DataTableVariant.small}
        noTableBorder
      />
    </Stack>
  );
};
